import { Loader } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import adminIllustration from "../../Assets/Images/Login/admin_illustration.png";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { userError } from "../../utils/toastUtils";

const AdminLogin = () => {
  return (
    <div className="h-screen flex overflow-hidden">
      <div className="w-full relative flex">
        <div className="w-full relative">
          <svg
            viewBox="0 0 500 150"
            preserveAspectRatio="none"
            style={{ height: "100%", width: "100%" }}
          >
            <defs>
              <radialGradient id="sakhiSpotlight" cx="50%" cy="100%" r="70%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
                <stop offset="40%" stopColor="rgba(255,255,255,0.25)" />
                <stop offset="70%" stopColor="rgba(255,255,255,0.08)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </radialGradient>

              <linearGradient id="sakhiGradient" gradientTransform="rotate(90)">
                <stop offset="0%" stopColor="#F7BFAD" />
                <stop offset="60%" stopColor="#f6a98f" />
                <stop offset="100%" stopColor="#f0653a" />
              </linearGradient>

              <linearGradient id="sakhiOverlay" gradientTransform="rotate(90)">
                <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>
            </defs>

            <path
              d="M208.09,0 
                C152.70,67.10 
                262.02,75.98 
                200.80,150.00 
                L0.00,150.00 
                L0.00,0.00 Z"
              fill="url(#sakhiGradient)"
            />
            <rect
              x="0"
              y="0"
              width="500"
              height="150"
              fill="url(#sakhiSpotlight)"
            />
            <path
              d="M180,0 
                C130,50 
                220,70 
                170,150 
                L0,150 
                L0,0 Z"
              fill="url(#sakhiOverlay)"
            />
            <circle cx="70" cy="40" r="18" fill="rgba(255,255,255,0.25)" />
            <circle cx="120" cy="95" r="10" fill="rgba(255,255,255,0.18)" />
          </svg>

          <div className="absolute hidden left-8 top-8 lg:flex items-center flex-col">
            <img src="/logo.png" className="h-20" alt="logo" />
          </div>

          <img
            src={adminIllustration}
            alt="Sakhi admin and staff"
            className="absolute left-8 md:left-4 lg:left-14 bottom-0 w-full md:w-[23rem] lg:w-[25rem] xl:w-[28rem] max-w-[90%]
                        pointer-events-none select-none"
          />
        </div>
      </div>

      <div className="absolute md:hidden inset-0 bg-black/80" />

      <div
        className="absolute w-[80%] ring-1 ring-accentTextColor lg:ring-0 bg-white/50 rounded-xl
                        py-8 left-1/2 -translate-x-1/2 max-w-[27rem]
                        top-1/2 -translate-y-1/2 md:left-[70%]"
      >
        <AdminLoginForm />
      </div>
    </div>
  );
};

const AdminLoginForm = () => {
  const {
    requestAdminOtp,
    verifyAdminOtp,
    resendAdminOtp,
    apiLoading: isLoading,
  } = useAdminAuth();

  const OTP_LENGTH = 4;

  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [expiresIn, setExpiresIn] = useState(0);

  const otpRefs = useRef([]);
  const timerRef = useRef(null);

  const isValidPhone = phoneNumber.length === 10;

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) setPhoneNumber(value);
  };

  const startTimer = (seconds) => {
    setExpiresIn(seconds);
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setExpiresIn((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const submitPhone = async (e) => {
    e.preventDefault();

    if (!isValidPhone) {
      userError("Phone number must be exactly 10 digits");
      return;
    }

    const res = await requestAdminOtp({ phoneNumber });

    if (!res?.success) return;

    startTimer(res.expiresIn);
    setStep("otp");
  };

  const resendOtp = async () => {
    const res = await resendAdminOtp({ phoneNumber });
    if (!res?.success) return;
    startTimer(res.expiresIn);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") submitOtp(e);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasted.length !== OTP_LENGTH) return;
    setOtp(pasted.split(""));
    otpRefs.current[OTP_LENGTH - 1]?.focus();
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== OTP_LENGTH) {
      userError("Enter valid OTP");
      return;
    }
    await verifyAdminOtp({ phoneNumber, otp: enteredOtp });
  };

  const goBack = () => {
    clearInterval(timerRef.current);
    setOtp(Array(OTP_LENGTH).fill(""));
    setStep("phone");
  };

  useEffect(() => {
    if (step === "otp") {
      otpRefs.current[0]?.focus();
    }
  }, [step]);

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div className="flex flex-col px-6">
      <div className="flex items-center flex-col">
        <img src="/logo.png" className="h-14 lg:hidden" alt="logo" />
      </div>

      <div className="mb-4 lg:mb-10 text-center">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
          {step === "phone" ? "Admin Login" : "Enter OTP"}
        </h1>
        <p className="text-xs hidden md:block sm:text-sm text-gray-800 lg:text-gray-600 mt-2">
          Access Sakhi administration panel
        </p>
      </div>

      <form
        onSubmit={step === "phone" ? submitPhone : submitOtp}
        className="space-y-5"
      >
        {step === "phone" && (
          <input
            type="tel"
            value={phoneNumber}
            inputMode="numeric"
            maxLength={10}
            onChange={handlePhoneChange}
            placeholder="Enter 10 digit mobile number"
            className="w-full rounded-lg px-4 py-3 tracking-wide ring-1 ring-accentTextColor outline-none"
          />
        )}

        {step === "otp" && (
          <>
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-11 h-11 text-center text-lg rounded-lg ring-1 ring-accentTextColor outline-none"
                />
              ))}
            </div>

            <div className="text-sm text-center text-gray-600">
              {expiresIn > 0 ? (
                <>
                  OTP expires in{" "}
                  <span className="font-semibold">{expiresIn}s</span>
                </>
              ) : (
                <button
                  type="button"
                  onClick={resendOtp}
                  className="text-[#f0653a] font-medium"
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={goBack}
              className="text-sm text-[#f0653a] text-left"
            >
              ← Change number
            </button>
          </>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-12 rounded-lg bg-[#f0653a] text-white font-semibold flex items-center justify-center"
        >
          {isLoading ? (
            <Loader className="animate-spin" size={20} />
          ) : step === "phone" ? (
            "Send OTP"
          ) : (
            "Verify & Login"
          )}
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
